import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import api from "api/axiosConfig";
import API_BASE from "configs/hostConfig";
import Text from 'ui/Text';
import theme from 'ui/theme/theme';
import {
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  useBreakpointValue,
} from '@chakra-ui/react';
import { TbAdjustmentsHorizontal } from 'react-icons/tb';
import { FaSearch } from 'react-icons/fa';
import EssayCard from './components/EssayCard';
import StudentEssayFiterPanel from './components/StudentEssayFilterPanel';

import { STUDENT_PUBLISH_STATUS_MAP as STATUS_MAP } from 'utils/data/options';
import { STOP_WORDS } from 'utils/data/constants';

const DefaultFilters = Object.freeze({ statusFilters: [], levelFilters: [] });
const stopWords = new Set(STOP_WORDS);

function StudentEssayPage() {
  const user = useSelector((state) => state.user?.info);

  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(
    JSON.parse(JSON.stringify(DefaultFilters))
  );
  const [essays, setEssays] = useState([]);
  const [matchedEssays, setMatchedEssays] = useState([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const essayColumns = useBreakpointValue({ base: 1, md: 2, lg: 3 });

  const fetchUserEssays = async () => {
    try {
      const { data } = await api.get(`${API_BASE}/api/student-essay/completed`);
      // console.log('user essays data: ', data);
      setEssays(data.data);
      setMatchedEssays(data.data);
    } catch (error) {
      console.error('Failed to fetch user essays:', error);
    }
  };

  useEffect(() => {
    fetchUserEssays();
  }, [user]);

  function filterEssaysByKeywords(essayList) {
    const keywords = filters?.keywords ?? '';
    if (!keywords || !essayList?.length) return essayList;

    const keywordArray = keywords
      .toLowerCase()
      .split(' ')
      .filter((word) => !stopWords.has(word));

    const keywordEssays = essayList.filter((essay) => {
      const lowercaseTitle = essay.title.toLocaleLowerCase();
      return keywordArray.some((keyword) => lowercaseTitle.includes(keyword));
    });

    return keywordEssays;
  }

  function filterEssaysByType(essayList) {
    const type = filters?.type ? filters.type.toLocaleLowerCase() : '';
    if (!essayList?.length || !type) return essayList;

    return essayList.filter((essay) => essay.primaryType === type);
  }

  function filterEssaysByStatuses(essayList) {
    const statusList = [];
    filters.statusFilters?.map((item) => {
      statusList.push(STATUS_MAP[item]);
    });
    if (!essayList?.length || !statusList?.length) return essayList;

    return essayList.filter((essay) =>
      statusList.includes(essay?.publishStatus ?? '')
    );
  }

  function getMatchedEssays() {
    if (!essays?.length) return;
    let matched = filterEssaysByKeywords(essays);

    if (matched?.length) {
      matched = filterEssaysByStatuses(matched);
    }

    if (matched?.length) {
      matched = filterEssaysByType(matched);
    }
    setMatchedEssays(matched);
  }

  useEffect(() => {
    getMatchedEssays();
  }, [filters]);

  function searchEssays(keywords) {
    setFilters({ ...filters, keywords });
  }

  function handleSearch(e) {
    if (e.code === 'Enter') {
      searchEssays(e.target.value);
    }
  }

  function filtersNumber() {
    let number = 0;
    if (filters?.statusFilters?.length) {
      number += 1;
    }
    if (filters?.levelFilters?.length) {
      number += 1;
    }
    if (filters?.type) {
      number += 1;
    }
    if (filters?.category) {
      number += 1;
    }
    return '(' + number + ')';
  }

  return (
    <Flex flexDirection="column">
      <Heading size="lg">My Essay Gallery</Heading>
      <Flex
        alignItems="center"
        mt="16px"
        mb="24px"
        justifyContent="space-between"
      >
        <InputGroup w="60%">
          <InputLeftElement ml="10px" mt="6px">
            <FaSearch
              onClick={() => searchEssays(query)}
              style={{ cursor: 'pointer' }}
            />
          </InputLeftElement>
          <Input
            id="searchEssays"
            onKeyDown={handleSearch}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            paddingLeft="56px"
            type="text"
            placeholder="Search essay title"
            maxW={{ base: 'unset', md: '354px' }}
            w="100%"
            h="48px"
            _placeholder={{ fontSize: '1rem' }}
            // borderRadius="52px"
            bg="gray.50"
          />
        </InputGroup>
        <Flex>
          <Flex
            as="button"
            p="12px 18px"
            ml="12px"
            alignItems="center"
            background="gray.50"
            border={`1px solid ${theme.colors.gray[200]}`}
            borderRadius="4px"
            _hover={{ bg: 'gray.300' }}
            onClick={() => setFilterDrawerOpen(true)}
          >
            <Text size="medium" mr="10px" fontWeight="bold">
              Filters {filtersNumber()}
            </Text>
            <TbAdjustmentsHorizontal size="24px" />
          </Flex>
        </Flex>
      </Flex>
      <Flex flexWrap="wrap">
        <SimpleGrid
          w="100%"
          columns={essayColumns}
          gap={{ base: '8px', md: '20px' }}
          rowGap="48px"
        >
          {matchedEssays?.map((essay) => (
            <EssayCard
              key={essay.essayId}
              id={essay.essayId}
              title={essay.title}
              essayType={essay.essayType}
              status={essay.status}
              score={essay.score}
              url={essay.url}
              // dueDate={essay.dueDate}
              finished
            />
          ))}
        </SimpleGrid>
      </Flex>
      <StudentEssayFiterPanel
        isOpen={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        currentFilters={filters}
        setFilters={setFilters}
      />
    </Flex>
  );
}

export default StudentEssayPage;
